import { useState, useEffect } from "react";
import Loader from "components/Loader";
import ArticleHeadlinesKeywordTable from "components/Tables/ArticleHeadlinesKeywordTable";
import MobileSupportTabBar from "components/TabBars/MobileSupportTabBar";
import HelpCard from "components/Cards/HelpCard";
import { Button, Col, Form } from "react-bootstrap";
import Validator from "utils/Validator";
import { get_seo_image_alt_tags_for_single_keyword } from "services/buddyXApiHubService";
import classNames from "classnames";
import { css } from "aphrodite";
import pageStyles from "styles/pageStyles";
import InsufficientCreditsPopup from "components/InsufficientCreditsPopup";
import { getInputFieldStyle, getButtonStyle } from "utils/formStyles";
import ToastAlert from "components/Alert/ToastAlert";

export default function Page() {
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResultGot, setIsResultGot] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [brandName, setBrandName] = useState("");
  const [showSearchItem, setShowSearchItem] = useState("");
  const [insufficientCreditsData, setInsufficientCreditsData] = useState(null);
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [formData, setFormData] = useState({
    brand_name: "",
    keywords: [],
  });
  const [errors, setErrors] = useState({ keyword: "", brandName: "" });
  const [tabs, setTabs] = useState([]);
  const firstLevelTopic = "SEO";
  const secoundLevelTopic = "Image";
  const thirdLevelTopic = "Optimisation";

  const handleKeywordChange = (e) => {
    setKeyword(e.target.value);
  };

  const handleBrandNameChange = (e) => {
    setBrandName(e.target.value);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let error = "";
    if (
      name === "keyword" &&
      !Validator.validateRequired(value, Validator.validateSingleKeyword)
    ) {
      error = "Please enter keywords";
    } else if (
      name === "brandName" &&
      !Validator.validateOptional(value, Validator.validateBrandName)
    ) {
      error = "Please enter brand name";
    }
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  const handleReset = () => {
    setShowSearchItem("");
    setIsResultGot(false);
    setKeyword("");
    setBrandName("");
    setShowErrorNotification(false);
    setErrorMessage("");
    setErrors({ keyword: "", brandName: "" });
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const result = await get_seo_image_alt_tags_for_single_keyword(
        formData,
        handleInsufficientCredits
      );
      setShowSearchItem(formData.keywords[0]);
      setTabs(
        Object.keys(result).map((key, index) => ({
          label: `Draft ${String.fromCharCode(65 + index)}`,
          element: (
            <ArticleHeadlinesKeywordTable
              data={result[key]}
              topic={thirdLevelTopic}
            />
          ),
        }))
      );
      setIsResultGot(true);
    } catch (error) {
      console.error("Submission error:", error);
      setErrorMessage(
        error.message || "Failed to generate image ALT tags. Please try again."
      );
      setShowErrorNotification(true);
      setIsResultGot(false);
    }
    setIsLoading(false);
  };

  const handleInsufficientCredits = (balanceData) => {
    setInsufficientCreditsData(balanceData);
    setShowInsufficientCredits(true);
  };

  const closeInsufficientCredits = () => {
    setShowInsufficientCredits(false);
    setInsufficientCreditsData(null);
  };

  useEffect(() => {
    let isValid =
      Validator.validateOptional(brandName, Validator.validateBrandName) &&
      Validator.validateRequired(keyword, Validator.validateSingleKeyword);
    if (isValid) {
      setFormData({
        brand_name: brandName,
        keywords: [keyword],
      });
    }
    setIsFormValid(isValid);
  }, [brandName, keyword]);

  return (
    <div className={classNames("container-fluid", css(pageStyles.container))}>
      {/* Toast Notifications */}
      <ToastAlert
        show={showErrorNotification}
        message={errorMessage}
        type="error"
        icon="fas fa-exclamation-circle"
        duration={5000}
        onClose={() => setShowErrorNotification(false)}
        position="top-right"
      />
      <h1 className="d-flex fw-normal justify-content-start align-items-center fw-bold">
        {secoundLevelTopic}
      </h1>
      <p className="fw-normal justify-content-start align-items-center">
        {firstLevelTopic} / {secoundLevelTopic} / <b>{thirdLevelTopic}</b>
      </p>
      <p className="fw-normal justify-content-start align-items-center">
        Optimizing image ALT tags at the given keyword level enhances image
        discoverability in search engines, particularly Google's image search,
        by embedding semantic relevance that aligns with user queries and
        supports accessibility standards like WCAG. This practice contributes to
        on-page SEO signals, potentially increasing image-driven traffic by
        15-30% in 2025's visual-centric search landscape, where AI-enhanced
        indexing prioritizes descriptive, intent-matched alt text to improve
        rankings, reduce bounce rates, and bolster topical authority without
        over-relying on textual content alone.
      </p>
      <div className="row">
        <div className="col-lg-9 col-md-10 col-sm-10">
          <HelpCard
            topic={"How To Use"}
            description={
              "Identify the given keyword and incorporate it naturally into ALT attributes for relevant images using HTML <img src='image.jpg' alt='Descriptive text including [Given Keyword]'> or CMS editors like WordPress Media Library, ensuring descriptions are concise (under 125 characters), unique per image, and contextually descriptive rather than keyword-stuffed. Prioritize high-impact images such as product visuals or infographics."
            }
            image={"/icon/pin.svg"}
          />
        </div>
      </div>
      <div className="p-2 mb-2 rounded">
        <div className="d-flex fw-bold mb-0">Generate ALT Tags</div>
        <div className="row mb-2 align-items-end">
          <div className="mb-2">
            <p className="fw-normal justify-content-start align-items-center">
              Enter your keyword and brand name to generate image ALT tags.
            </p>
            <Form.Group>
              <div className="d-flex mt-2">
                <Col sm={4} className="me-2">
                  <Form.Control
                    type="text"
                    name="keyword"
                    placeholder="Enter keyword"
                    value={keyword}
                    onChange={handleKeywordChange}
                    onBlur={handleBlur}
                    isInvalid={!!errors.keyword}
                    required
                    className="px-2"
                    style={getInputFieldStyle('type6')}
                  />
                </Col>
                <Col sm={4} className="me-2">
                  <Form.Control
                    type="text"
                    name="brandName"
                    placeholder="Enter Brand Name"
                    value={brandName}
                    onChange={handleBrandNameChange}
                    onBlur={handleBlur}
                    isInvalid={!!errors.brandName}
                    className="px-2"
                    style={getInputFieldStyle('type6')}
                  />
                </Col>
                <div className="col-lg-4 col-md-4 col-sm-12 mb-2">
                  <div className="d-flex justify-content-start align-items-end">
                    <Button
                      variant="dark"
                      className="me-1"
                      onClick={handleSubmit}
                      disabled={!isFormValid || isLoading}
                      style={getButtonStyle('type6', 'primary')}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Generating...
                        </>
                      ) : (
                        "Search"
                      )}
                    </Button>
                    <Button
                      variant="outline-dark"
                      className="me-1"
                      onClick={handleReset}
                      style={getButtonStyle('type6', 'outline')}
                    >
                      Reset
                    </Button>
                  </div>
                </div>
              </div>
            </Form.Group>
          </div>
        </div>
      </div>

      <div className="p-2">
        {showSearchItem !== "" && (
          <div>
            <p className="d-flex fw-bold mb-0">
              ALT tags for
              <span className="search-item">&nbsp;"{showSearchItem}"</span>
            </p>
          </div>
        )}
        {isLoading && <Loader />}
        {isResultGot && <MobileSupportTabBar tabs={tabs} />}
      </div>
      {/* Insufficient Credits Popup */}
      <InsufficientCreditsPopup
        isOpen={showInsufficientCredits}
        onClose={closeInsufficientCredits}
        currentBalance={insufficientCreditsData?.currentBalance}
        requiredCredits={insufficientCreditsData?.requiredCredits}
        shortfall={insufficientCreditsData?.shortfall}
        actionName={insufficientCreditsData?.actionName}
      />
    </div>
  );
}
